import { Dispatch, SetStateAction, useContext, useState } from 'react';

import { Button } from '@selina-finance/ui';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { isHelpToBuyNotRepaid } from '@FormModels/eligibility/yourProperty';
import { MarkedOffer } from '@Pages/offer/Explore';
import { getFormattedProduct } from '@Shared/helpers/string';
import { ApplicationContext } from '@src/app/contexts/Application';
import { OfferContext } from '@src/app/contexts/Offer';
import { Offer } from '@src/app/interfaces/offer';
import { toCurrency } from '@src/shared/helpers';
import OfferHelp from '@src/ui/components/Help/OfferHelp';
import { HttpError } from '@src/ui/components/HttpError';
import OfferCard from '@src/ui/components/OfferCard/OfferCard';

interface IExploreViewProps {
    markedOffer: MarkedOffer;
    offers: Offer[];
    selectedOffer: Offer | null;
    onSelect: Dispatch<SetStateAction<Offer | null>>;
    hasDeclinedOffer: boolean;
    applicationId: string;
}

const ExploreView = ({
    offers,
    selectedOffer,
    onSelect,
    markedOffer,
    hasDeclinedOffer,
    applicationId,
}: IExploreViewProps) => {
    const { propertyDetails } = useContext(ApplicationContext);
    const { saveSelectedOffer } = useContext(OfferContext);
    const navigate = useNavigate();

    const isHtbCustomer = isHelpToBuyNotRepaid(
        propertyDetails?.ownershipSchemeType,
        propertyDetails?.ownershipSchemeRepaid,
    );
    const [error, setError] = useState<string | null>(null);

    const [previousSelectedOffer, setPreviousSelectedOffer] =
        useState<Offer | null>(selectedOffer);

    const [expanded, setExpanded] = useState(false);

    const saveOffer = async () => {
        if (selectedOffer !== previousSelectedOffer) {
            const product = getFormattedProduct(selectedOffer?.product);

            mixpanel.track('Select offer and continue', {
                'Product selected': product,
                application_id: applicationId,
            });

            const response = await saveSelectedOffer();
            if (response.success) {
                setPreviousSelectedOffer(selectedOffer);
                navigate(
                    isHtbCustomer
                        ? '/eligibility/offer/htb-next-steps'
                        : '/eligibility/offer/finalise',
                );
            } else {
                setError(response.error);
            }
        } else {
            navigate(
                isHtbCustomer
                    ? '/eligibility/offer/htb-next-steps'
                    : '/eligibility/offer/finalise',
            );
        }
    };

    return (
        <div className='flex flex-col items-center mx-auto max-w-400 w-full lg:max-w-none'>
            <p className='text-2xl lg:text-4xl font-sora-bold leading-120% pt-6 sm:pt-0 tracking-tighter'>
                {`Choose your quote for £${toCurrency(
                    offers[0]?.netLoanAmount,
                )} over ${offers[0]?.term} years`}
            </p>
            <h2 className='text-xl leading-120% pt-2 pb-7 text-midnight-200'>
                Based on what you told us, here are some options - subject to
                underwriting
            </h2>
            <div className='flex lg:w-full flex-col lg:flex-row justify-center'>
                {offers.map((offer) => (
                    <OfferCard
                        selectable
                        hasDeclinedOffer={hasDeclinedOffer}
                        key={offer.id}
                        offer={offer}
                        onSelect={() => onSelect(offer)}
                        expanded={expanded}
                        toggle={() => setExpanded((prev) => !prev)}
                        selected={offer.id === selectedOffer?.id}
                        starLabel={
                            offer.productCode === markedOffer.code
                                ? markedOffer.label
                                : undefined
                        }
                    />
                ))}
            </div>
            <div className='flex flex-col text-center items-center w-full lg:max-w-400'>
                <p className='text-sm leading-120% mb-10 lg:mb-6 lg:mt-6'>
                    Your home may be repossessed if you do not keep up
                    repayments on a mortgage or any other debt secured on it
                </p>
                <Button
                    color='primary'
                    className='w-full mb-2 lg:max-w-400'
                    size='large'
                    onClick={saveOffer}
                    disabled={!selectedOffer}
                >
                    Select this quote
                </Button>
                <div className='text-sm mb-4'>
                    <p className='font-bold mb-1'>
                        This won&apos;t impact your credit score
                    </p>
                    <p className='leading-120% mb-10'>
                        You&apos;ll be able to review before submitting an
                        application
                    </p>
                </div>
                <OfferHelp />
                {error && <HttpError error={error} />}
            </div>
        </div>
    );
};

export default ExploreView;
